// EditDesign.js - Performance-optimised version
// - Preloads original product images once
// - Precomputes tinted canvases for all colours in background using requestIdleCallback
// - Instant colour switching (no loading, no delays)
import "../AddDesign/AddDesign.css"
import { Stage, Layer, Image as KonvaImage, Transformer, Rect } from "react-konva";
import useImage from "use-image";
import { Fragment, useRef, useEffect, useState, useCallback } from "react";
import { UilImageDownload, UilTrashAlt, UilCheck } from "@iconscout/react-unicons";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import { useSelector } from "react-redux";

// â”€â”€â”€ Axios instance (no interceptor) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000/api",
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' }
});

// ============================================
// PIXEL MANIPULATION HELPERS (same as before)
// ============================================
const BACKGROUND_TOLERANCE = 60;
const TRANSITION_ZONE = 3;
const TRANSPARENCY_DETECTION_THRESHOLD = 0.05;

const parseColor = (color) => {
  if (!color) return [200, 200, 200];
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) return [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])];
  const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbaMatch) return [parseInt(rgbaMatch[1]), parseInt(rgbaMatch[2]), parseInt(rgbaMatch[3])];
  if (color.startsWith('#')) {
    let hex = color.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    return [parseInt(hex.slice(0,2),16), parseInt(hex.slice(2,4),16), parseInt(hex.slice(4,6),16)];
  }
  try {
    const div = document.createElement('div');
    div.style.color = color; div.style.display = 'none';
    document.body.appendChild(div);
    const computed = window.getComputedStyle(div).color;
    document.body.removeChild(div);
    const m = computed.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (m) return [parseInt(m[1]), parseInt(m[2]), parseInt(m[3])];
  } catch (e) {}
  return [200, 200, 200];
};

const hasExistingTransparency = (data, w, h) => {
  let transparentCount = 0;
  let total = 0;
  for (let x = 0; x < w; x++) {
    if (data[x * 4 + 3] < 10) transparentCount++;
    total++;
    const bi = ((h - 1) * w + x) * 4;
    if (data[bi + 3] < 10) transparentCount++;
    total++;
  }
  for (let y = 0; y < h; y++) {
    if (data[(y * w) * 4 + 3] < 10) transparentCount++;
    total++;
    if (data[(y * w + w - 1) * 4 + 3] < 10) transparentCount++;
    total++;
  }
  return (transparentCount / total) >= TRANSPARENCY_DETECTION_THRESHOLD;
};

const colorDistRGB = (r1,g1,b1,r2,g2,b2) =>
  Math.sqrt((r1-r2)**2 + (g1-g2)**2 + (b1-b2)**2);

const getBackgroundAlphaMap = (data, w, h) => {
  const bgR = data[0], bgG = data[1], bgB = data[2];
  const alphaMap = new Float32Array(w * h).fill(255);
  const visited = new Uint8Array(w * h);
  const queue = [];

  const isBackground = (idx) =>
    colorDistRGB(data[idx*4], data[idx*4+1], data[idx*4+2], bgR, bgG, bgB) <= BACKGROUND_TOLERANCE;

  for (let x = 0; x < w; x++) {
    [x, (h-1)*w+x].forEach(idx => {
      if (!visited[idx] && isBackground(idx)) { visited[idx]=1; alphaMap[idx]=0; queue.push(idx); }
    });
  }
  for (let y = 0; y < h; y++) {
    [y*w, y*w+(w-1)].forEach(idx => {
      if (!visited[idx] && isBackground(idx)) { visited[idx]=1; alphaMap[idx]=0; queue.push(idx); }
    });
  }

  while (queue.length > 0) {
    const idx = queue.pop();
    const x = idx % w, y = Math.floor(idx / w);
    const neighbors = [
      y > 0 ? idx-w : -1,
      y < h-1 ? idx+w : -1,
      x > 0 ? idx-1 : -1,
      x < w-1 ? idx+1 : -1,
    ];
    for (const nIdx of neighbors) {
      if (nIdx < 0 || nIdx >= w*h || visited[nIdx]) continue;
      if (isBackground(nIdx)) { visited[nIdx]=1; alphaMap[nIdx]=0; queue.push(nIdx); }
    }
  }
  const smoothAlpha = new Float32Array(alphaMap);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y*w+x;
      if (alphaMap[idx] > 0) continue;
      for (let dy = -TRANSITION_ZONE; dy <= TRANSITION_ZONE; dy++) {
        for (let dx = -TRANSITION_ZONE; dx <= TRANSITION_ZONE; dx++) {
          const nx = x+dx, ny = y+dy;
          if (nx<0||nx>=w||ny<0||ny>=h) continue;
          const nIdx = ny*w+nx;
          if (alphaMap[nIdx] === 0) continue;
          const dist = Math.sqrt(dx*dx+dy*dy);
          if (dist <= TRANSITION_ZONE) {
            const fade = (dist/TRANSITION_ZONE)**2 * 255;
            if (fade < smoothAlpha[nIdx]) smoothAlpha[nIdx] = fade;
          }
        }
      }
    }
  }
  return smoothAlpha;
};

const buildTintedCanvas = (img, color) => {
  const w = img.width;
  const h = img.height;
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);

  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;
  const [tR, tG, tB] = parseColor(color || "#cccccc");
  const colorLuma = (0.299 * tR + 0.587 * tG + 0.114 * tB) / 255;

  const alreadyTransparent = hasExistingTransparency(data, w, h);
  const alphaMap = alreadyTransparent ? null : getBackgroundAlphaMap(data, w, h);

  for (let i = 0; i < data.length; i += 4) {
    const pixelIdx = i / 4;
    const or = data[i], og = data[i+1], ob = data[i+2], oa = data[i+3];
    const finalAlpha = alreadyTransparent
      ? oa
      : (alphaMap[pixelIdx] < 1 ? 0 : Math.round((oa/255) * alphaMap[pixelIdx]));
    if (finalAlpha < 1) {
      data[i]=0; data[i+1]=0; data[i+2]=0; data[i+3]=0;
      continue;
    }
    const brightness = (or + og + ob) / 3;
    const factor = brightness / 255;
    let oR = Math.min(255, Math.round(tR * factor));
    let oG = Math.min(255, Math.round(tG * factor));
    let oB = Math.min(255, Math.round(tB * factor));
    if (colorLuma < 0.25) {
      const darkness = 1 - colorLuma / 0.25;
      const overlay = Math.round((brightness / 255) * 60 * darkness);
      oR = Math.min(255, oR + overlay);
      oG = Math.min(255, oG + overlay);
      oB = Math.min(255, oB + overlay);
    }
    data[i] = oR; data[i+1] = oG; data[i+2] = oB;
    data[i+3] = finalAlpha;
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
};

// ============================================
// OPTIMIZED COLOUR CACHE with background precomputation
// ============================================
const colourCache = new Map();
const originalImageCache = new Map();

const preloadOriginalImage = (imageUrl) => {
  if (!imageUrl) return Promise.resolve(null);
  if (originalImageCache.has(imageUrl)) {
    const cached = originalImageCache.get(imageUrl);
    if (cached.complete) return Promise.resolve(cached);
    return new Promise((resolve) => {
      cached.onload = () => resolve(cached);
    });
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    let src = imageUrl;
    if (imageUrl.includes('ovh.net')) src = `${imageUrl}?t=${Date.now()}`;
    img.onload = () => {
      originalImageCache.set(imageUrl, img);
      resolve(img);
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
};

const precomputeColour = async (imageUrl, color) => {
  if (!imageUrl || !color) return;
  const cacheKey = `${imageUrl}|${color}`;
  if (colourCache.has(cacheKey)) return;
  const img = await preloadOriginalImage(imageUrl);
  if (!img) return;
  const tinted = buildTintedCanvas(img, color);
  colourCache.set(cacheKey, tinted);
};

const getTintedCanvas = async (imageUrl, color) => {
  if (!imageUrl || !color) return null;
  const cacheKey = `${imageUrl}|${color}`;
  if (colourCache.has(cacheKey)) return colourCache.get(cacheKey);
  const img = await preloadOriginalImage(imageUrl);
  if (!img) return null;
  const tinted = buildTintedCanvas(img, color);
  colourCache.set(cacheKey, tinted);
  return tinted;
};

const useTintedCanvas = (imageUrl, color) => {
  const [canvas, setCanvas] = useState(null);
  useEffect(() => {
    if (!imageUrl || !color) {
      setCanvas(null);
      return;
    }
    let cancelled = false;
    getTintedCanvas(imageUrl, color).then((result) => {
      if (!cancelled && result) setCanvas(result);
    });
    return () => { cancelled = true; };
  }, [imageUrl, color]);
  return { canvas, isLoading: false };
};

// ============================================
// DESIGN ITEM COMPONENT (unchanged)
// ============================================
const DesignItem = ({ design, isSelected, onSelect, onUpdate, onDelete }) => {
  const imageRef = useRef();
  const trRef = useRef();
  const [deleteIcon] = useImage("/corbeille.png");

  useEffect(() => {
    if (isSelected && trRef.current && imageRef.current) {
      trRef.current.nodes([imageRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  const handleDragMove = (e) => {
    onUpdate({ ...design, x: e.target.x(), y: e.target.y() });
  };
  const handleTransform = (e) => {
    const node = imageRef.current;
    onUpdate({
      ...design,
      x: node.x(),
      y: node.y(),
      scaleX: node.scaleX(),
      scaleY: node.scaleY(),
      rotation: node.rotation()
    });
  };
  const handleClick = (e) => {
    e.cancelBubble = true;
    onSelect(design.id);
  };
  const handleDeleteClick = (e) => {
    e.cancelBubble = true;
    onDelete(design.id);
  };
  const displayWidth = design.originalWidth;
  const displayHeight = design.originalHeight;

  return (
    <Fragment>
      <KonvaImage
        image={design.image}
        x={design.x}
        y={design.y}
        width={displayWidth}
        height={displayHeight}
        scaleX={design.scaleX || 1}
        scaleY={design.scaleY || 1}
        rotation={design.rotation || 0}
        onClick={handleClick}
        onTap={handleClick}
        onDragMove={handleDragMove}
        onTransform={handleTransform}
        ref={imageRef}
        draggable
        imageSmoothingEnabled={true}
        imageSmoothingQuality="high"
      />
      {isSelected && (
        <Fragment>
          <Transformer
            ref={trRef}
            anchorSize={10}
            borderEnabled={true}
            borderStroke="#0B2A5C"
            borderStrokeWidth={2}
            rotateEnabled={true}
            rotationSnaps={[0, 90, 180, 270]}
            keepRatio={true}
            enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
            anchorStyleFunc={(anchor) => {
              anchor.cornerRadius(5);
              anchor.fill('#0B2A5C');
              anchor.stroke('#ffffff');
              anchor.strokeWidth(2);
            }}
          />
          {deleteIcon && (
            <KonvaImage
              image={deleteIcon}
              onClick={handleDeleteClick}
              width={24}
              height={24}
              onMouseOver={() => (document.body.style.cursor = "pointer")}
              onMouseOut={() => (document.body.style.cursor = "default")}
              x={design.x + (displayWidth * (design.scaleX || 1)) / 2 - 12}
              y={design.y + (displayHeight * (design.scaleY || 1)) + 10}
            />
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const isGalleryImage = (image) =>
  image?.role === "gallery" || (typeof image?.type === "string" && image.type.startsWith("gallery"));

const getStoredImageUrl = (image) =>
  image?.url || image?.original || image?.medium || image?.thumbnail || "";

const formatFileSize = (size = 0) =>
  size < 1024 * 1024 ? `${Math.max(1, Math.round(size / 1024))} KB` : `${(size / (1024 * 1024)).toFixed(1)} MB`;

export const EditDesign = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useSelector((state) => state.auth);

  // â”€â”€ Sync token to axios after Redux rehydration â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    const rawToken = currentUser?.token || null;
    const refreshToken = currentUser?.refreshToken || null;
    if (rawToken) {
      const token = rawToken.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
      api.defaults.headers.common["Authorization"] = token;
      console.log("Admin token synced to axios (EditDesign)");
    } else {
      delete api.defaults.headers.common["Authorization"];
      console.warn("No token in currentUser — admin may not be logged in");
    }
    if (refreshToken) {
      api.defaults.headers.common["x-refresh-token"] = refreshToken;
    } else {
      delete api.defaults.headers.common["x-refresh-token"];
    }
  }, [currentUser]);

  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingDesign, setLoadingDesign] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);

  const [formData, setFormData] = useState({
    _id: "", title: "", theme: "", cost: 0, discount: 0,
    description: "", price: 0, tags: [], designType: ["DTF"], isPublishedB2B: true, isPublishedAdmin: true, imgs: { front: "", back: "" }
  });
  const [tagInput, setTagInput] = useState("");
  const [displayedColor, setDisplayedColor] = useState("");
  const [selectedColors, setSelectedColors] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [frontDesigns, setFrontDesigns] = useState([]);
  const [backDesigns, setBackDesigns] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  const [selectedSide, setSelectedSide] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [themes, setThemes] = useState([]);

  const frontStageRef = useRef(null);
  const backStageRef = useRef(null);
  const galleryImagesRef = useRef([]);

  useEffect(() => {
    galleryImagesRef.current = galleryImages;
  }, [galleryImages]);

  useEffect(() => {
    return () => {
      galleryImagesRef.current.forEach((image) => {
        if (image.mode === "new" && image.previewUrl) URL.revokeObjectURL(image.previewUrl);
      });
    };
  }, []);

  const getProductImageUrl = (product, side = 'front') => {
    if (!product?.imgs?.length) return null;
    const image = product.imgs.find(img => img.type === side);
    if (image) return image.original || image.medium || image.thumbnail || null;
    const fallback = product.imgs[side === 'front' ? 0 : 1] || product.imgs[0];
    return fallback?.original || fallback?.medium || fallback?.thumbnail || null;
  };

  const productFrontImageUrl = getProductImageUrl(selectedProduct, 'front');
  const productBackImageUrl = getProductImageUrl(selectedProduct, 'back');

  const { canvas: tintedFront } = useTintedCanvas(productFrontImageUrl, displayedColor);
  const { canvas: tintedBack } = useTintedCanvas(productBackImageUrl, displayedColor);

  // Background precomputation of all colours after product loads
  useEffect(() => {
    if (!selectedProduct || !availableColors.length) return;
    const urls = [productFrontImageUrl, productBackImageUrl].filter(Boolean);
    if (!urls.length) return;

    let idleCallbackId;
    const precomputeAll = () => {
      for (const url of urls) {
        for (const color of availableColors) {
          precomputeColour(url, color);
        }
      }
    };
    idleCallbackId = requestIdleCallback ? requestIdleCallback(precomputeAll, { timeout: 2000 }) : setTimeout(precomputeAll, 500);
    return () => {
      if (idleCallbackId) {
        if (cancelIdleCallback) cancelIdleCallback(idleCallbackId);
        else clearTimeout(idleCallbackId);
      }
    };
  }, [selectedProduct, availableColors, productFrontImageUrl, productBackImageUrl]);

  const getColorsFromProduct = (product) => {
    if (!product) return [];
    if (product.colorsAndSizes && typeof product.colorsAndSizes === 'object' && !Array.isArray(product.colorsAndSizes)) {
      return Object.keys(product.colorsAndSizes);
    }
    if (Array.isArray(product?.colorsAndSizes)) return product.colorsAndSizes.map(c => c.color);
    return [];
  };

  const loadDesignImageFromUrl = async (imageUrl, side, metadataArray) => {
    return new Promise((resolve) => {
      if (!imageUrl) {
        if (side === "front") setFrontDesigns([]);
        else setBackDesigns([]);
        resolve();
        return;
      }
      let retryCount = 0;
      const maxRetries = 3;
      const loadImage = () => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        let srcUrl = imageUrl;
        if (imageUrl.includes('ovh.net')) srcUrl = `${imageUrl}?t=${Date.now()}&retry=${retryCount}`;
        img.onload = () => {
          let designs = [];
          if (metadataArray && metadataArray.length > 0) {
            designs = metadataArray.map((meta, idx) => ({
              id: `${Date.now()}_${idx}_${Math.random()}`,
              image: img,
              x: meta.x || 0,
              y: meta.y || 0,
              originalWidth: meta.originalWidth || img.width,
              originalHeight: meta.originalHeight || img.height,
              scaleX: meta.scaleX || 1,
              scaleY: meta.scaleY || 1,
              rotation: meta.rotation || 0,
              name: meta.name || `design_${idx}`
            }));
          } else if (img.width > 0 && img.height > 0) {
            const initialScale = Math.min(0.8, 400 / Math.max(img.width, img.height));
            const initialWidth = img.width * initialScale;
            const initialHeight = img.height * initialScale;
            const initialX = (500 - initialWidth) / 2;
            const initialY = (500 - initialHeight) / 2;
            designs = [{
              id: `${Date.now()}_0_${Math.random()}`,
              image: img,
              x: initialX,
              y: initialY,
              originalWidth: img.width,
              originalHeight: img.height,
              scaleX: initialScale,
              scaleY: initialScale,
              rotation: 0,
              name: `design_${side}`
            }];
          }
          if (side === "front") setFrontDesigns(designs);
          else setBackDesigns(designs);
          resolve();
        };
        img.onerror = () => {
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(loadImage, 500);
          } else {
            if (side === "front") setFrontDesigns([]);
            else setBackDesigns([]);
            resolve();
          }
        };
        img.src = srcUrl;
      };
      loadImage();
    });
  };

  const loadDesignData = useCallback(async () => {
    try {
      setLoadingDesign(true);
      const res = await api.get(`/designs/${id}`);
      const design = res.data.design;
      if (!design) {
        toast.error("Design non trouvé");
        navigate("/designs");
        return;
      }
      setFormData({
        _id: design._id,
        title: design.title || "",
        theme: design.theme || "",
        cost: design.cost || 0,
        discount: design.discount || 0,
        description: design.description || "",
        price: design.price || 0,
        tags: design.tags || [],
        designType: design.designType?.length ? design.designType : ["DTF"],
        isPublishedB2B: design.isPublishedB2B !== false,
        isPublishedAdmin: design.isPublishedAdmin !== false,
        imgs: design.imgs?.[0] || { front: "", back: "" }
      });
      const existingGalleryImages = (design.imgs || [])
        .filter(isGalleryImage)
        .map((image, index) => ({
          id: image.url || image.original || `${image.type || "gallery"}-${index}`,
          mode: "existing",
          image,
          previewUrl: getStoredImageUrl(image),
          name: image.originalName || image.type || `image-${index + 1}`,
          size: image.size || 0,
          type: image.type || `gallery-${index + 1}`,
        }));
      setGalleryImages(existingGalleryImages);
      if (design.linkedProduct) {
        setProductId(design.linkedProduct);
        await loadProductData(design.linkedProduct);
      }
      const frontImg = design.imgs?.find(img => img.type === 'front');
      const backImg = design.imgs?.find(img => img.type === 'back');
      const frontImageUrl = frontImg?.url || null;
      const backImageUrl = backImg?.url || null;
      await Promise.all([
        loadDesignImageFromUrl(frontImageUrl, "front", design.designMetadata?.front),
        loadDesignImageFromUrl(backImageUrl, "back", design.designMetadata?.back)
      ]);
    } catch (err) {
      console.error("Error loading design:", err);
      toast.error(err.response?.data?.message || "Erreur lors du chargement du design");
      navigate("/designs");
    } finally {
      setLoadingDesign(false);
    }
  }, [id, navigate]);

  const loadProductData = async (productId) => {
    try {
      const res = await api.get(`/products/${productId}`);
      if (res.data.product) {
        setSelectedProduct(res.data.product);
        const colors = getColorsFromProduct(res.data.product);
        setAvailableColors(colors);
        const designRes = await api.get(`/designs/${id}`);
        const design = designRes.data.design;
        if (design.availableColors && design.availableColors.length > 0) {
          setSelectedColors(design.availableColors);
          setDisplayedColor(design.availableColors[0] || (colors[0] || ""));
        } else {
          setSelectedColors(colors);
          setDisplayedColor(colors[0] || "");
          toast.info("Ce modèle utilise toutes les couleurs du produit", { autoClose: 3000 });
        }
      }
    } catch (err) { console.error("Error loading product:", err); }
  };

  const fetchProductsForDropdown = useCallback(async () => {
    try {
      const timestamp = Date.now();
      const res = await api.get(`/products/designs?_t=${timestamp}`);
      setProducts(res.data.products || []);
    } catch (err) {
      console.error("Error fetching products:", err);
      toast.error("Erreur lors du chargement des produits");
    }
  }, []);

  const fetchThemes = useCallback(async () => {
    try {
      const res = await api.get("/themes");
      setThemes(res.data.themes || []);
    } catch (err) { console.error("Erreur chargement themes:", err); }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      await fetchProductsForDropdown();
      await fetchThemes();
      await loadDesignData();
      setLoading(false);
    };
    loadData();
  }, [fetchProductsForDropdown, fetchThemes, loadDesignData]);

  const chooseProduct = async (productId) => {
    if (!productId) {
      setProductId(""); setSelectedProduct(null); setDisplayedColor("");
      setSelectedColors([]); setAvailableColors([]);
      return;
    }
    setProductId(productId);
    setLoadingProduct(true);
    try {
      const res = await api.get(`/products/${productId}`);
      if (res.data.product) {
        setSelectedProduct(res.data.product);
        const colors = getColorsFromProduct(res.data.product);
        setAvailableColors(colors);
        setSelectedColors(colors);
        setDisplayedColor(colors[0] || "");
        toast.success(`Produit chargé: ${res.data.product._id}`, { autoClose: 2000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors du chargement du produit");
      setSelectedProduct(null);
    } finally { setLoadingProduct(false); }
  };

  const isSelectedProductFinished = selectedProduct?.isFinishedProduct === true;

  const handleColorToggle = (color) => {
    if (selectedColors.includes(color)) {
      setSelectedColors(prev => prev.filter(c => c !== color));
      if (displayedColor === color) {
        const newSelected = selectedColors.filter(c => c !== color);
        setDisplayedColor(newSelected.length > 0 ? newSelected[0] : "");
      }
    } else {
      setSelectedColors(prev => [...prev, color]);
      if (!displayedColor) setDisplayedColor(color);
    }
  };

  const handleSelectAllColors = () => {
    setSelectedColors([...availableColors]);
    if (availableColors.length > 0 && !displayedColor) setDisplayedColor(availableColors[0]);
    toast.success(`Toutes les couleurs sélectionnées (${availableColors.length})`);
  };
  const handleDeselectAllColors = () => {
    setSelectedColors([]);
    setDisplayedColor("");
    toast.info(`Aucune couleur sélectionnée`);
  };

  const handleDeleteDesign = (side, designId) => {
    if (side === "front") setFrontDesigns(prev => prev.filter(d => d.id !== designId));
    else setBackDesigns(prev => prev.filter(d => d.id !== designId));
    if (selectedDesignId === designId) { setSelectedDesignId(null); setSelectedSide(null); }
    toast.success(`Design supprimé`, { autoClose: 1500 });
  };

  const handleDesignUpload = (side, e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const existingCount = (side === "front" ? frontDesigns.length : backDesigns.length) + index;
          const originalWidth = img.width, originalHeight = img.height;
          const initialScale = Math.min(0.8, 400 / Math.max(originalWidth, originalHeight));
          const initialWidth = originalWidth * initialScale;
          const initialHeight = originalHeight * initialScale;
          const initialX = (500 - initialWidth) / 2 + (existingCount * 20);
          const initialY = (500 - initialHeight) / 2 + (existingCount * 20);
          const newDesign = {
            id: `${Date.now()}_${index}_${Math.random()}`,
            image: img,
            x: initialX, y: initialY,
            originalWidth, originalHeight,
            scaleX: initialScale, scaleY: initialScale,
            rotation: 0,
            name: file.name
          };
          if (side === "front") setFrontDesigns(prev => [...prev, newDesign]);
          else setBackDesigns(prev => [...prev, newDesign]);
        };
      };
      reader.readAsDataURL(file);
    });
    toast.success(`${files.length} design(s) ajouté(s) sur le ${side === 'front' ? 'devant' : 'dos'}`, { autoClose: 2000 });
    e.target.value = null;
  };

  const handleGalleryImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newImages = files.map((file, index) => ({
      id: `${Date.now()}_${index}_${Math.random()}`,
      mode: "new",
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
    }));

    setGalleryImages((prev) => [...prev, ...newImages]);
    toast.success(`${files.length} image(s) ajoutee(s) au modele`, { autoClose: 2000 });
    e.target.value = null;
  };

  const handleDeleteGalleryImage = (imageId) => {
    setGalleryImages((prev) => {
      const imageToRemove = prev.find((image) => image.id === imageId);
      if (imageToRemove?.mode === "new" && imageToRemove.previewUrl) URL.revokeObjectURL(imageToRemove.previewUrl);
      return prev.filter((image) => image.id !== imageId);
    });
    toast.success("Image supprimee", { autoClose: 1500 });
  };

  const handleUpdateDesign = (side, updatedDesign) => {
    if (side === "front") {
      setFrontDesigns(prev => prev.map(d => d.id === updatedDesign.id ? { ...updatedDesign, originalWidth: d.originalWidth, originalHeight: d.originalHeight, image: d.image } : d));
    } else {
      setBackDesigns(prev => prev.map(d => d.id === updatedDesign.id ? { ...updatedDesign, originalWidth: d.originalWidth, originalHeight: d.originalHeight, image: d.image } : d));
    }
  };

  const handleSelectDesign = (side, designId) => { setSelectedDesignId(designId); setSelectedSide(side); };
  const handleBackgroundClick = (side) => { setSelectedDesignId(null); setSelectedSide(null); };

  const downloadURI = (uri, name) => {
    const link = document.createElement("a");
    link.download = name; link.href = uri;
    document.body.appendChild(link); link.click();
    document.body.removeChild(link);
  };

  const handleExport = (side) => {
    const stageRef = side === "front" ? frontStageRef : backStageRef;
    if (!stageRef.current) return;
    const transformer = stageRef.current.findOne('Transformer');
    if (transformer) transformer.visible(false);
    const exportSize = 3840;
    const scale = exportSize / 500;
    const dataURL = stageRef.current.toDataURL({ mimeType: "image/png", pixelRatio: scale, quality: 1 });
    if (transformer) transformer.visible(true);
    downloadURI(dataURL, `${formData.title || 'design'}-${side}-4k.png`);
    toast.success(`Design exporté en 4K`, { autoClose: 2000 });
  };

  const getTotalCost = () => (parseFloat(selectedProduct?.purchase) || 0) + (parseFloat(formData.cost) || 0);
  const getPriceAfterDiscount = () => {
    const price = parseFloat(formData.price) || 0;
    const discount = parseFloat(formData.discount) || 0;
    return discount > 0 ? price * (1 - discount / 100) : price;
  };

  const handleUpdate = async () => {
    if (!formData.title) { toast.error('Veuillez entrer un titre'); return; }
    if (!selectedProduct?._id) { toast.error('Veuillez sélectionner un produit'); return; }
    if (!isSelectedProductFinished && selectedColors.length === 0) {
      toast.error('Veuillez sélectionner au moins une couleur pour ce design');
      return;
    }
    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("linkedProduct", selectedProduct._id);
      formDataToSend.append("price", parseFloat(formData.price) || 0);
      formDataToSend.append("cost", parseFloat(formData.cost) || 0);
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append("theme", formData.theme);
      formDataToSend.append("discount", parseFloat(formData.discount) || 0);
      formDataToSend.append("tags", JSON.stringify(formData.tags || []));
      formDataToSend.append("designType", JSON.stringify(formData.designType?.length ? formData.designType : ["DTF"]));
      formDataToSend.append("isPublishedB2B", formData.isPublishedB2B.toString());
      formDataToSend.append("isPublishedAdmin", formData.isPublishedAdmin.toString());
      formDataToSend.append("rate", 5);
      formDataToSend.append("isFinishedProduct", isSelectedProductFinished);
      formDataToSend.append("availableColors", JSON.stringify(selectedColors));
      formDataToSend.append("designMetadata", JSON.stringify({
        front: frontDesigns.map(d => ({ name: d.name, x: d.x, y: d.y, originalWidth: d.originalWidth, originalHeight: d.originalHeight, scaleX: d.scaleX, scaleY: d.scaleY, rotation: d.rotation })),
        back: backDesigns.map(d => ({ name: d.name, x: d.x, y: d.y, originalWidth: d.originalWidth, originalHeight: d.originalHeight, scaleX: d.scaleX, scaleY: d.scaleY, rotation: d.rotation })),
        gallery: galleryImages.map((image, index) => ({ name: image.name, size: image.size, type: image.type || `gallery-${index + 1}` }))
      }));

      if (!isSelectedProductFinished) {
        const imageTypes = [];
        const keptExistingGalleryImages = galleryImages
          .filter((image) => image.mode === "existing" && image.image)
          .map((image) => image.image);

        formDataToSend.append("existingImages", JSON.stringify(keptExistingGalleryImages));

        for (const design of frontDesigns) {
          const canvas = document.createElement('canvas');
          canvas.width = design.originalWidth; canvas.height = design.originalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(design.image, 0, 0, design.originalWidth, design.originalHeight);
          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
          const file = new File([blob], `front-${design.name || Date.now()}.png`, { type: 'image/png' });
          formDataToSend.append("images", file);
          imageTypes.push("front");
        }
        for (const design of backDesigns) {
          const canvas = document.createElement('canvas');
          canvas.width = design.originalWidth; canvas.height = design.originalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(design.image, 0, 0, design.originalWidth, design.originalHeight);
          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
          const file = new File([blob], `back-${design.name || Date.now()}.png`, { type: 'image/png' });
          formDataToSend.append("images", file);
          imageTypes.push("back");
        }
        galleryImages
          .filter((image) => image.mode === "new")
          .forEach((image, index) => {
            formDataToSend.append("images", image.file);
            imageTypes.push(`gallery-${keptExistingGalleryImages.length + index + 1}`);
          });
        if (imageTypes.length > 0) formDataToSend.append("imageTypes", JSON.stringify(imageTypes));
      }

      await api.put(`/designs/${id}`, formDataToSend, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(isSelectedProductFinished ? 'Produit fini mis a jour avec succes!' : `${selectedColors.length} couleur(s) selectionnee(s) - ${frontDesigns.length + backDesigns.length} design(s), ${galleryImages.length} image(s) mis a jour!`);
      setTimeout(() => navigate("/designs"), 2000);
    } catch (err) {
      console.error("Update error:", err);
      toast.error(err.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally { setIsSubmitting(false); }
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !formData.tags?.includes(trimmed)) {
      setFormData({ ...formData, tags: [...formData.tags, trimmed] });
      setTagInput("");
    }
  };

  const colors = availableColors;

  if (loading || loadingDesign) {
    return (
      <div className="add-product-container">
        <div className="add-product-grid">
          <div className="add-product-grid-item">
            <h1>Modifier le modèle</h1>
            <div className="loading-spinner-large"><div className="spinner-large" /><p>Chargement du design...</p></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-product-container">
      <div className="add-product-grid">
        <div className="add-product-grid-item">
          <h1>✏️ Modifier le modèle: {formData.title}</h1>
          {!currentUser && <div className="auth-alert">⚠️ Vous devez être connecté en tant qu'administrateur</div>}

          <div className="add-product-categories">
            <div>
              <h4>Choix produit</h4>
              <select className="add-product-input" value={productId || ""} onChange={(e) => chooseProduct(e.target.value)} disabled={loadingProduct}>
                <option value="">-- Sélectionner un produit --</option>
                {products?.map((p) => (
                  <option key={p._id} value={p._id}>{p.displayName || `${p._id} (${p.fournisseur || 'No supplier'})`} {p.isFinishedProduct ? '🏷️' : '🎨'}</option>
                ))}
              </select>
              {loadingProduct && <div className="field-note">Chargement du produit...</div>}
            </div>
            <div><h4>Catégorie</h4><div className="add-product-details"><input type="text" readOnly value={selectedProduct?.category || ""} className="add-product-input-text" /></div></div>
            <div><h4>Sous-catégorie</h4><div className="add-product-details"><input type="text" readOnly value={selectedProduct?.subcategory || ""} className="add-product-input-text" /></div></div>
          </div>

          {isSelectedProductFinished && (
            <div className="quantity-container" style={{ background: 'var(--success-soft)', marginBottom: '1rem' }}>
              <h4>🏷️ Produit Fini</h4>
              <div className="field-note">Ce produit est un produit fini. Il n'a pas besoin de design personnalisé.</div>
              <div className="field-note">Le prix de vente suggéré est basé sur le prix d'achat avec une marge de 50%.</div>
              <div className="field-note">Vous pouvez modifier le prix ci-dessous.</div>
            </div>
          )}

          {!isSelectedProductFinished && colors.length > 0 && (
            <div className="quantity-container" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '10px' }}>
                <h4 style={{ margin: 0 }}>🎨 Sélection des couleurs pour ce design</h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" onClick={handleSelectAllColors} style={{ padding: '4px 12px', fontSize: '11px' }}>Tout sélectionner</button>
                  <button type="button" onClick={handleDeselectAllColors} style={{ padding: '4px 12px', fontSize: '11px', background: '#666' }}>Tout désélectionner</button>
                </div>
              </div>
              <div className="color-selection-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                {colors.map((color) => (
                  <div key={color} onClick={() => handleColorToggle(color)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '8px', borderRadius: '12px', background: selectedColors.includes(color) ? 'var(--accent-soft)' : 'transparent', border: selectedColors.includes(color) ? '1.5px solid var(--accent)' : '1.5px solid transparent', transition: 'all 0.2s ease' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: color, border: '2px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', position: 'relative' }}>
                      {selectedColors.includes(color) && (<div style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '20px', height: '20px', background: 'var(--accent)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 'bold' }}><UilCheck size={14} color="white" /></div>)}
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-secondary)' }}>{color}</span>
                  </div>
                ))}
              </div>
              <div className="field-note">{selectedColors.length === 0 ? <span style={{ color: 'var(--danger)' }}>⚠️ Sélectionnez au moins une couleur pour ce design</span> : <span style={{ color: 'var(--success)' }}>✅ {selectedColors.length} couleur(s) sélectionnée(s) pour ce modèle</span>}</div>
            </div>
          )}

          <div className="form-group"><h4>Nom du modèle / produit</h4><div className="add-product-input"><input onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} className="add-product-input-text" placeholder="Ex: T-shirt club ossec" value={formData.title} /></div></div>
          <div className="form-group"><h4>Description</h4><div className="add-product-input"><textarea onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="add-product-input-paragraph" placeholder="Description..." value={formData.description} /></div></div>

          <div className="add-product-price">
            <div><h4>Prix d'achat produit</h4><div className="add-product-input"><input readOnly type="text" className="add-product-input-text" value={selectedProduct?.purchase || "0"} /></div><div className="field-note">💰 Prix d'achat du produit vierge (fournisseur)</div></div>
            <div><h4>Coût de production</h4><div className="add-product-input"><input type="number" className="add-product-input-text" placeholder="Ex: 5.00" onChange={(e) => setFormData(p => ({ ...p, cost: Number(e.target.value) }))} value={formData.cost} min="0" step="0.01" /></div><div className="field-note">🎨 Coût de sérigraphie / impression du design</div></div>
            <div><h4>Coût total (revient)</h4><div className="add-product-input" style={{ background: "var(--accent-soft)" }}><input readOnly type="text" className="add-product-input-text" value={getTotalCost().toFixed(2)} style={{ fontWeight: "600", color: "var(--accent)" }} /></div><div className="field-note">📊 = Prix d'achat + Coût de production</div></div>
            <div><h4>Prix de vente</h4><div className="add-product-input"><input onChange={(e) => setFormData(p => ({ ...p, price: Number(e.target.value) }))} type="number" className="add-product-input-text" placeholder="Ex: 29.99" value={formData.price} min="0" step="0.01" /></div><div className="field-note">🏷️ Prix final pour le client</div></div>
          </div>

          <div className="form-group"><h4>Promotion (%)</h4><div className="add-product-input"><input onChange={(e) => setFormData(p => ({ ...p, discount: Number(e.target.value) }))} type="number" className="add-product-input-text" placeholder="0" value={formData.discount} min="0" max="100" /></div>{formData.discount > 0 && <div className="field-note" style={{ color: 'var(--success)' }}>🎉 Prix après promotion: €{getPriceAfterDiscount().toFixed(2)}</div>}</div>
          <div className="form-group"><h4>Thème</h4><select className="add-product-input" value={formData.theme} onChange={(e) => setFormData({ ...formData, theme: e.target.value })}><option value="">Sélectionner un thème</option>{themes?.map((theme) => <option key={theme._id} value={theme.title}>{theme.title}</option>)}</select></div>

          <div className="quantity-container">
            <h4>Quantité disponible</h4>
            <div className="add-product-colors-quantity">
              {!selectedProduct ? <h4 style={{ color: "#dc3545", textAlign: "center" }}>Choisissez un produit d'abord</h4>
              : isSelectedProductFinished ? (Object.keys(selectedProduct.sizesOnly || {}).length === 0 ? <h4 style={{ color: "#dc3545", textAlign: "center" }}>Aucune taille définie pour ce produit</h4>
                : <div className="color-sizes color-sizes--active">{Object.entries(selectedProduct.sizesOnly).map(([size, quantity]) => <div key={size}><h4>{size}:</h4><input readOnly type="text" value={quantity} /></div>)}</div>)
              : colors.length === 0 ? <h4 style={{ color: "#dc3545", textAlign: "center" }}>Aucune couleur disponible pour ce produit</h4>
              : selectedColors.map((colorName, index) => {
                  const sizes = selectedProduct.colorsAndSizes[colorName];
                  return (<div className={`color-sizes ${displayedColor === colorName ? 'color-sizes--active' : ''}`} key={index}>
                    <div className="choose-color" onClick={() => setDisplayedColor(colorName)} style={{ backgroundColor: colorName }}><button type="button" /></div>
                    {sizes && Object.entries(sizes).map(([size, quantity]) => <div key={size}><h4>{size}:</h4><input readOnly type="text" value={quantity} /></div>)}
                  </div>);
                })}
            </div>
            <div className="field-note">📦 Quantités disponibles {isSelectedProductFinished ? 'par taille' : 'pour les couleurs sélectionnées'}</div>
          </div>

          <div className="form-group">
            <h4>Tags du produit vierge</h4>
            <div className="add-product-tags">{selectedProduct?.tags?.map((tag, index) => <span key={index} style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{tag}<span style={{ fontSize: "0.7rem", marginLeft: "4px", opacity: 0.6 }}>(produit)</span></span>)} {(!selectedProduct?.tags || selectedProduct.tags.length === 0) && <div className="field-note" style={{ color: "var(--text-muted)", padding: "0.5rem 0" }}>Aucun tag associé à ce produit</div>}</div>
            <div style={{ marginTop: "1rem" }}><h4>Tags du modèle (design)</h4><div className="add-product-tags">{formData.tags?.map((tag, index) => (<span key={index}>{tag}<button type="button" onClick={() => setFormData({ ...formData, tags: formData.tags.filter((_, i) => i !== index) })}>&times;</button></span>))}</div><div className="add-product-tags-input"><input type="text" placeholder="Ajouter un tag pour ce modèle" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddTag(); } }} /><button type="button" onClick={handleAddTag}>Ajouter</button></div><div className="field-note" style={{ marginTop: "0.5rem" }}>🏷️ Les tags du produit sont hérités du produit vierge. Ajoutez des tags spécifiques à ce modèle.</div></div>
          </div>

          <div className="form-group">
            <h4>Type d'impression</h4>
            <div className="add-product-tags">
              {["Sublimation", "DTF", "Broderie"].map((option) => (
                <label key={option} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", marginRight: "1rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={(formData.designType || []).includes(option)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => {
                        const current = prev.designType || [];
                        return {
                          ...prev,
                          designType: checked ? [...current, option] : current.filter((t) => t !== option),
                        };
                      });
                    }}
                  />
                  {option}
                </label>
              ))}
            </div>
            <div className="field-note">Aucune sélection = DTF par défaut.</div>
          </div>

          <div className="form-group">
            <h4>Publication</h4>
            <div className="add-product-tags" style={{ gap: "0.5rem" }}>
              <button
                type="button"
                onClick={() => setFormData((p) => ({ ...p, isPublishedB2B: !p.isPublishedB2B }))}
                style={{ background: formData.isPublishedB2B ? "var(--success, #22c55e)" : undefined }}
              >
                {formData.isPublishedB2B ? "✔ Publié dans B2B" : "Publier dans B2B"}
              </button>
              <button
                type="button"
                onClick={() => setFormData((p) => ({ ...p, isPublishedAdmin: !p.isPublishedAdmin }))}
                style={{ background: formData.isPublishedAdmin ? "var(--success, #22c55e)" : undefined }}
              >
                {formData.isPublishedAdmin ? "✔ Publié dans Admin" : "Publier dans Admin"}
              </button>
            </div>
            <div className="field-note">
              Publié dans B2B = visible dans la boutique client. Publié dans Admin = sélectionnable dans "Nv Commande" par le staff.
            </div>
          </div>

          {!isSelectedProductFinished && selectedColors.length > 0 && (
            <>
              <div className="design-gallery-section">
                <div className="design-gallery-header">
                  <div>
                    <h4>Images du modele ({galleryImages.length})</h4>
                    <div className="field-note">Ajoutez ou supprimez les images detail du modele. Elles restent sauvegardees sur OVH dans le dossier designs.</div>
                  </div>
                  <span className="design-gallery-pill">OVH designs</span>
                </div>
                <div className="add-product-input"><input type="file" accept="image/*" onChange={handleGalleryImageUpload} multiple /></div>
                {galleryImages.length > 0 && (
                  <div className="design-gallery-grid">
                    {galleryImages.map((image, index) => (
                      <div className="design-gallery-card" key={image.id}>
                        <img src={image.previewUrl} alt={image.name} />
                        <button type="button" className="design-gallery-remove" onClick={() => handleDeleteGalleryImage(image.id)} title="Supprimer l'image"><UilTrashAlt size={16} /></button>
                        <div className="design-gallery-meta">
                          <span>{index + 1}. {image.name}</span>
                          <small>{image.mode === "existing" ? "Deja sur OVH" : formatFileSize(image.size)}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ borderTop: '2px solid var(--border)', marginTop: '1.5rem', paddingTop: '1.5rem', marginBottom: '1rem' }}><h3 style={{ fontSize: '1rem', textTransform: 'none', color: 'var(--accent)', marginBottom: '0.5rem', fontWeight: 600 }}>📥 Téléchargement des designs (Haute qualité)</h3></div>
              <div className="form-group"><div className="flex-row"><div style={{ flex: 1 }}><h4 style={{ fontSize: "0.8rem", marginBottom: "0.5rem" }}>📤 Design devant ({frontDesigns.length})</h4><div className="add-product-input"><input type="file" accept="image/*" onChange={(e) => handleDesignUpload("front", e)} multiple /></div><div className="field-note">PNG, JPG, SVG - Plusieurs fichiers autorisés - Haute résolution préservée</div></div><div style={{ flex: 1 }}><h4 style={{ fontSize: "0.8rem", marginBottom: "0.5rem" }}>📤 Design dos ({backDesigns.length})</h4><div className="add-product-input"><input type="file" accept="image/*" onChange={(e) => handleDesignUpload("back", e)} multiple /></div><div className="field-note">PNG, JPG, SVG - Plusieurs fichiers autorisés - Haute résolution préservée</div></div></div></div>
              {(frontDesigns.length > 0 || backDesigns.length > 0) && (<div className="quantity-container" style={{ marginBottom: '1rem', background: 'var(--accent-soft)' }}><h4>📊 Résumé des designs</h4><div className="field-note" style={{ fontWeight: 500 }}>Devant: {frontDesigns.length} design(s) | Dos: {backDesigns.length} design(s)</div><div className="field-note">💡 Cliquez sur un design dans l'aperçu pour le déplacer/redimensionner</div><div className="field-note">🗑️ Cliquez sur la corbeille pour supprimer un design</div><div className="field-note">⭐ Les designs sont sauvegardés en haute résolution</div></div>)}
            </>
          )}

          <div className="publish-buttons">
            <button onClick={handleUpdate} className="B2B-button" disabled={isSubmitting || (!isSelectedProductFinished && selectedColors.length === 0)}>{isSubmitting ? "Mise à jour en cours..." : `💾 Mettre à jour le design (${selectedColors.length} couleur${selectedColors.length > 1 ? 's' : ''})`}</button>
            <button type="button" onClick={() => navigate("/designs")} style={{ background: "#666" }}>Annuler</button>
          </div>
        </div>

        <div className="add-product-grid-item preview-panel">
          {!isSelectedProductFinished && selectedColors.length > 0 && (<div className="color-picker-bar"><span className="color-picker-label">🎨 Couleur</span><div className="color-picker-dots">{selectedColors.map((c) => (<button key={c} className={`color-picker-dot ${displayedColor === c ? 'active' : ''}`} style={{ backgroundColor: c }} onClick={() => setDisplayedColor(c)} title={c} />))}</div>{displayedColor && <span className="color-picker-value">{displayedColor}</span>}</div>)}
          {!isSelectedProductFinished && colors.length > 0 && selectedColors.length === 0 && (<div className="color-picker-bar" style={{ justifyContent: 'center', opacity: 0.6 }}><span className="color-picker-label">🎨 Sélectionnez une couleur ci-dessus</span></div>)}

          <div className="preview-section">
            <div className="preview-section-header"><h3>👕 DEVANT {frontDesigns.length > 0 && `(${frontDesigns.length} design${frontDesigns.length > 1 ? 's' : ''})`}</h3>{!isSelectedProductFinished && frontDesigns.length > 0 && (<button className="export-btn" onClick={() => handleExport("front")}><UilImageDownload size={14} /> Exporter 4K</button>)}</div>
            <div className="preview-canvas">
              {!isSelectedProductFinished ? (
                <Stage ref={frontStageRef} width={500} height={500} style={{ imageSmoothingEnabled: true, imageSmoothingQuality: 'high' }}>
                  <Layer><Rect x={0} y={0} width={500} height={500} fill="white" onClick={() => handleBackgroundClick("front")} />
                  {tintedFront && <KonvaImage image={tintedFront} width={500} height={500} imageSmoothingEnabled={true} imageSmoothingQuality="high" onClick={() => handleBackgroundClick("front")} />}
                  {frontDesigns.map((design) => (<DesignItem key={design.id} design={design} isSelected={selectedDesignId === design.id && selectedSide === "front"} onSelect={() => handleSelectDesign("front", design.id)} onUpdate={(updated) => handleUpdateDesign("front", updated)} onDelete={() => handleDeleteDesign("front", design.id)} />))}</Layer>
                </Stage>
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: '20px' }}>
                  {productFrontImageUrl ? <img src={productFrontImageUrl} alt="Product front" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <p>Image devant non disponible</p>}
                </div>
              )}
            </div>
          </div>

          <div className="preview-section">
            <div className="preview-section-header"><h3>👕 DOS {backDesigns.length > 0 && `(${backDesigns.length} design${backDesigns.length > 1 ? 's' : ''})`}</h3>{!isSelectedProductFinished && backDesigns.length > 0 && (<button className="export-btn" onClick={() => handleExport("back")}><UilImageDownload size={14} /> Exporter 4K</button>)}</div>
            <div className="preview-canvas">
              {!isSelectedProductFinished ? (
                <Stage ref={backStageRef} width={500} height={500} style={{ imageSmoothingEnabled: true, imageSmoothingQuality: 'high' }}>
                  <Layer><Rect x={0} y={0} width={500} height={500} fill="white" onClick={() => handleBackgroundClick("back")} />
                  {tintedBack && <KonvaImage image={tintedBack} width={500} height={500} imageSmoothingEnabled={true} imageSmoothingQuality="high" onClick={() => handleBackgroundClick("back")} />}
                  {backDesigns.map((design) => (<DesignItem key={design.id} design={design} isSelected={selectedDesignId === design.id && selectedSide === "back"} onSelect={() => handleSelectDesign("back", design.id)} onUpdate={(updated) => handleUpdateDesign("back", updated)} onDelete={() => handleDeleteDesign("back", design.id)} />))}</Layer>
                </Stage>
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: '20px' }}>
                  {productBackImageUrl ? <img src={productBackImageUrl} alt="Product back" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <p>Image dos non disponible</p>}
                </div>
              )}
            </div>
          </div>

          {selectedProduct && <div className="product-loaded-badge">✅ Produit sélectionné: {selectedProduct._id}</div>}
        </div>
      </div>
      <ToastContainer position="top-right" />
    </div>
  );
};
